import { memo, useCallback, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { F0Icon, F0Link } from '@factorialco/f0-react'
import { PushPinSolid } from '@factorialco/f0-react/icons/app'
import { GetPinnedPostsDocument } from 'generated/resources/graphql'
import { F0Text } from '@factorialco/f0-react'
import { usePaginatedQuery } from 'modules/core/hooks/usePaginatedQuery'
import i18n from 'modules/core/lib/i18n'
import { truncatePostDescription } from 'modules/posts/lib/truncatePostDescription'

import './index.styles.css'

type PinnedPostsListProps = {
  groupId: string
  totalPinnedPostsCount: number
  isPostPinningEnabled: boolean
}

const PINNED_POSTS_FIRST = 20

const PinnedPostsList = ({
  groupId,
  totalPinnedPostsCount,
  isPostPinningEnabled,
}: PinnedPostsListProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)

  const { data, loading } = usePaginatedQuery(GetPinnedPostsDocument, {
    variables: {
      groups: [groupId],
      first: PINNED_POSTS_FIRST,
    },
    skip: !isPostPinningEnabled || totalPinnedPostsCount === 0,
    extractConnection: (connectionData) =>
      connectionData.posts?.postsConnection ?? {
        pageInfo: { hasNextPage: false, endCursor: null },
        edges: [],
      },
  })

  const edges = data?.posts?.postsConnection?.edges ?? []
  const totalLoaded = edges.length
  const total = totalPinnedPostsCount

  const goToIndex = useCallback(
    (index: number) => {
      setCurrentIndex(Math.max(0, Math.min(index, totalLoaded - 1)))
    },
    [totalLoaded]
  )

  if (!isPostPinningEnabled || totalPinnedPostsCount === 0) {
    return null
  }

  if (loading && totalLoaded === 0) {
    return (
      <Box className="PinnedPostsList_root" flexDirection="row" alignItems="stretch" gap="s16">
        <Box flexNoShrink>
          <F0Icon icon={PushPinSolid} color="#0a66c2" />
        </Box>
        <Box flexGrow>Loading pinned posts...</Box>
      </Box>
    )
  }

  if (totalLoaded === 0) {
    return null
  }

  const displayIndex = Math.min(currentIndex, totalLoaded - 1)
  const currentPost = edges[displayIndex]?.node

  const leanDescription = truncatePostDescription(currentPost?.description, 140)

  const maxBullets = 3
  const bulletIndices =
    totalLoaded <= maxBullets
      ? Array.from({ length: totalLoaded }, (_, i) => i)
      : (() => {
          const start = Math.max(0, Math.min(displayIndex - 1, totalLoaded - maxBullets))
          return [start, start + 1, start + 2]
        })()

  return (
    <Box
      padding="s8"
      className="PinnedPostsList_root"
      flexDirection="row"
      alignItems="stretch"
      gap="s16"
    >
      <Box flexNoShrink className="PinnedPostsList_iconWrapper">
        <F0Icon icon={PushPinSolid} color="#0a66c2" />
      </Box>
      <Box flexGrow flexDirection="column" alignItems="flexStart">
        <Box flexDirection="row" alignItems="center" className="PinnedPostsList_counter">
          {displayIndex + 1}/{total} {i18n.t('posts.communities.pinned_posts_label')}
        </Box>
        {currentPost != null && (
          <F0Link variant="unstyled" href={`#community-post-${currentPost.id}`}>
            <F0Text variant="body" content={leanDescription} as="p" style={{ fontWeight: 400 }} />
          </F0Link>
        )}
      </Box>
      <Box
        flexNoShrink
        flexDirection="column"
        gap="s4"
        alignItems="center"
        className="PinnedPostsList_bullets"
        data-testid="pinned-posts-bullets"
      >
        {bulletIndices.map((i) => (
          // eslint-disable-next-line factorial/no-raw-html -- Bullet pagination control
          <button
            key={i}
            type="button"
            aria-label={i18n.t('posts.communities.pinned_post_navigation', {
              current: i + 1,
              total,
            })}
            aria-current={i === displayIndex ? 'true' : undefined}
            className={`PinnedPostsList_bullet ${i === displayIndex ? 'PinnedPostsList_bulletActive' : ''}`}
            onClick={() => goToIndex(i)}
          />
        ))}
      </Box>
    </Box>
  )
}

export default memo(PinnedPostsList)
